<template>
  <v-layout
    row
    wrap
  >
    <v-flex
      v-if="querySubsets(query).length"
      xs2
    >
      <v-select
        v-model="query.subset"
        :items="querySubsets(query)"
        :item-disabled="itemDisabled"
        :label="$t('in subset')"
        dense
        solo
        clearable
        @change="handleChangeSubset"
      />
    </v-flex>
    <v-flex>
      <v-select
        v-model="query.rule"
        :items="operands"
        :item-disabled="itemDisabled"
        :item-text="text"
        :item-value="itemValue"
        dense
        solo
        @change="handleChangeOperand"
      />
    </v-flex>
    <v-flex xs2>
      <v-select
        v-model="query.selectedOperator"
        :items="_operators"
        :item-disabled="itemDisabled"
        item-text="text"
        item-value="value"
        append-icon
        solo
        @change="handleChangeOperator"
      >
        <template
          slot="selection"
          slot-scope="data"
        >
          <div class="selected">
            {{ $t(data.item.text || data.item) }}
          </div>
        </template>
      </v-select>
    </v-flex>
    <v-flex
      v-if="!unaryOperators.includes(query.selectedOperator)"
      :class="querySubsets(query).length ? 'xs3' : 'xs5'"
    >
      <v-select
        v-if="values"
        v-model="query.value"
        :items="values"
        :item-disabled="itemDisabled"
        :item-text="text"
        :item-value="itemValue"
        :multiple="query.type === 'checkbox'"
        single-line
        dense
        solo
        @change="handleChangeValue"
      />
      <v-text-field
        v-else-if="isMoneyQuery(query)"
        :value="moneyValue"
        dense
        solo
        @input="handleChangeMoneyValue"
        @keyup.native="handleEnterKeyUp"
      />
      <v-text-field
        v-else
        v-model="query.value"
        dense
        solo
        @input="handleChangeValue"
        @keyup.native="handleEnterKeyUp"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import Money from 'js-money'
import currencies from '@/../node_modules/js-money/lib/currency'
import { mapGetters } from 'vuex'

export default {
  props: {
    /**
     *
     */
    index: {
      type: String,
      default: '0'
    },
    position: {
      type: Number,
      default: 0
    },
    operators: {
      type: Array,
      required: true
    },
    operands: {
      type: Array,
      required: true
    },
    query: {
      type: Object,
      required: true
    },
    itemText: {
      type: String,
      default: 'text'
    },
    itemValue: {
      type: String,
      default: 'value'
    }
  },

  data () {
    return {
      unaryOperators: ['$null', '$not_null'],
      moneyValue: null,
      values: []
    }
  },

  computed: {
    ...mapGetters(['currency']),
    _operators () {
      return this.availableOperators()
    }
  },

  mounted () {
    if (this.isMoneyQuery(this.query)) {
      this.moneyValue = this.query.value !== ''
        ? Money.fromInteger({ amount: parseInt(this.query.value), currency: this.currency }) : null
    }

    this.queryValues(this.query)
  },

  methods: {
    text (val) {
      return this.$t(val[this.itemText] || val)
    },

    availableOperators () {
      const operandObject = this.operands.find(
        v => v.value === this.query.rule
      )

      if (operandObject && Array.isArray(operandObject.operators)) {
        return operandObject.operators
      }

      return this.operators
    },

    handleChangeOperand (operand) {
      const operandObject = this.operands.find(
        v => v.value === operand
      )
      this.query.selectedOperator = this.availableOperators()[0].value
      this.query.type = operandObject.type
      this.query.value = ''
      this.query.subset = ''
      this.values = []
      this.$emit('change-operand', operandObject, this.query, [this.position])

      this.queryValues(this.query)
    },

    handleChangeOperator (operator) {
      this.$emit('change-operator', operator, this.query, [this.position])
    },

    handleChangeValue (value) {
      this.$emit('change-value', value, this.query, [this.position])
    },

    handleChangeSubset (value) {
      if (!value) {
        delete this.query.subset
      }
    },

    handleChangeMoneyValue (value) {
      if (value !== '') {
        let currencyInfo = currencies[this.currency]
        // converting float value to integer money format
        value = parseInt(parseFloat(value).toFixed(currencyInfo.decimal_digits).toString().replace(/[,.]/g, ''))
      }

      this.query.value = value
      this.$emit('change-value', value, this.query, [this.position])
    },

    handleEnterKeyUp (event) {
      if (event && event.code === 'Enter') {
        this.$emit('enter-keyup', event)
      }
    },

    itemDisabled (select) {
      return select.disabled || false
    },

    async queryValues (query) {
      if (!query.rule) {
        this.values = null
        return
      }
      const operand = this.operands.find(v => v.value === query.rule)

      if (!operand) {
        this.values = null
        return
      }

      if (typeof operand.options === 'function') {
        if (this.values.length) {
          return
        }

        this.values = await operand.options(this.$http)
        return
      }

      this.values = operand.options
    },

    querySubsets (query) {
      if (!query.rule) {
        return []
      }
      const operand = this.operands.find(v => v.value === query.rule)

      if (!operand || !operand.subsets) {
        return []
      }

      return operand.subsets
    },

    isMoneyQuery (query) {
      if (!query.rule) {
        return false
      }
      const operand = this.operands.find(v => v.value === query.rule)

      if (!operand) {
        return false
      }

      return operand.subtype && operand.subtype === 'money'
    }
  }
}
</script>
